<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <!-- Background overlay -->
      <div class="fixed inset-0 transition-opacity" @click="close">
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- Modal panel -->
      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div class="sm:flex sm:items-start">
            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
              <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
                Manage Template Variables
              </h3>
              
              <!-- Add new variable form -->
              <div class="mb-6 p-4 border border-gray-200 rounded-md bg-gray-50">
                <h4 class="font-medium mb-3">Add New Variable</h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Variable Name</label>
                    <input
                      type="text"
                      v-model="newVariable.name"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                      placeholder="e.g., customerName"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <input
                      type="text"
                      v-model="newVariable.category"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                      placeholder="e.g., Customer"
                    />
                  </div>
                  <div class="md:col-span-2">
                    <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <input
                      type="text"
                      v-model="newVariable.description"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                      placeholder="e.g., Customer's full name"
                    />
                  </div>
                  <div class="md:col-span-2">
                    <label class="block text-sm font-medium text-gray-700 mb-1">Example Value</label>
                    <input
                      type="text"
                      v-model="newVariable.exampleValue"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                      placeholder="e.g., John Doe"
                    />
                  </div>
                </div>
                <div class="mt-3 flex justify-end">
                  <button
                    @click="addVariable"
                    class="px-3 py-1 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    :disabled="!isValidNewVariable"
                  >
                    Add Variable
                  </button>
                </div>
              </div>
              
              <!-- Variables list -->
              <div class="mt-4">
                <h4 class="font-medium mb-2">Available Variables</h4>
                
                <!-- Filter by category -->
                <div class="mb-3">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Filter by Category</label>
                  <select
                    v-model="selectedCategory"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                  >
                    <option value="">All Categories</option>
                    <option v-for="category in uniqueCategories" :key="category" :value="category">
                      {{ category }}
                    </option>
                  </select>
                </div>
                
                <!-- Variables table -->
                <div class="overflow-x-auto">
                  <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                      <tr>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Category
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Example
                        </th>
                        <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                      <tr v-for="(variable, index) in filteredVariables" :key="index">
                        <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {{ variable.name }}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {{ variable.category }}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {{ variable.description }}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {{ variable.exampleValue }}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            @click="editVariable(index)"
                            class="text-indigo-600 hover:text-indigo-900 mr-2"
                          >
                            Edit
                          </button>
                          <button
                            @click="confirmDelete(index)"
                            class="text-red-600 hover:text-red-900"
                            :disabled="variable.isSystem"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                      <tr v-if="filteredVariables.length === 0">
                        <td colspan="5" class="px-6 py-4 text-center text-sm text-gray-500">
                          No variables found
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              
              <!-- Edit variable modal -->
              <div v-if="editingVariable !== null" class="fixed inset-0 z-50 overflow-y-auto">
                <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                  <div class="fixed inset-0 transition-opacity" @click="cancelEdit">
                    <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
                  </div>
                  <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                      <h4 class="text-lg font-medium mb-4">Edit Variable</h4>
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label class="block text-sm font-medium text-gray-700 mb-1">Variable Name</label>
                          <input
                            type="text"
                            v-model="editedVariable.name"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                          />
                        </div>
                        <div>
                          <label class="block text-sm font-medium text-gray-700 mb-1">Category</label>
                          <input
                            type="text"
                            v-model="editedVariable.category"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                          />
                        </div>
                        <div class="md:col-span-2">
                          <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
                          <input
                            type="text"
                            v-model="editedVariable.description"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                          />
                        </div>
                        <div class="md:col-span-2">
                          <label class="block text-sm font-medium text-gray-700 mb-1">Example Value</label>
                          <input
                            type="text"
                            v-model="editedVariable.exampleValue"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                          />
                        </div>
                      </div>
                    </div>
                    <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                      <button
                        @click="saveEdit"
                        class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:ml-3 sm:w-auto sm:text-sm"
                      >
                        Save
                      </button>
                      <button
                        @click="cancelEdit"
                        class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Delete confirmation modal -->
              <div v-if="deletingVariableIndex !== null" class="fixed inset-0 z-60 overflow-y-auto">
                <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                  <div class="fixed inset-0 transition-opacity" @click="cancelDelete">
                    <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
                  </div>
                  <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                      <div class="sm:flex sm:items-start">
                        <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                          <svg class="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        </div>
                        <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                          <h3 class="text-lg leading-6 font-medium text-gray-900">
                            Delete Variable
                          </h3>
                          <div class="mt-2">
                            <p class="text-sm text-gray-500">
                              Are you sure you want to delete this variable? This action cannot be undone.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                      <button
                        @click="deleteVariable"
                        class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                      >
                        Delete
                      </button>
                      <button
                        @click="cancelDelete"
                        class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button
            @click="close"
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:ml-3 sm:w-auto sm:text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, PropType } from 'vue';
import { logger } from '@/services/logger';

export interface TemplateVariable {
  name: string;
  category: string;
  description: string;
  exampleValue: string;
  isSystem?: boolean;
}

export default defineComponent({
  name: 'VariableManagerModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    variables: {
      type: Array as PropType<TemplateVariable[]>,
      required: true
    }
  },
  emits: ['close', 'update:variables'],
  setup(props, { emit }) {
    const selectedCategory = ref('');
    const editingVariable = ref<number | null>(null);
    const deletingVariableIndex = ref<number | null>(null);
    const editedVariable = ref<TemplateVariable>({
      name: '',
      category: '',
      description: '',
      exampleValue: ''
    });
    
    const newVariable = ref<TemplateVariable>({
      name: '',
      category: '',
      description: '',
      exampleValue: ''
    });
    
    const isValidNewVariable = computed(() => {
      return newVariable.value.name.trim() !== '' && 
             newVariable.value.category.trim() !== '';
    });
    
    const uniqueCategories = computed(() => {
      const categories = new Set<string>();
      props.variables.forEach(variable => {
        if (variable.category) {
          categories.add(variable.category);
        }
      });
      return Array.from(categories).sort();
    });
    
    const filteredVariables = computed(() => {
      if (!selectedCategory.value) {
        return props.variables;
      }
      return props.variables.filter(variable => variable.category === selectedCategory.value);
    });
    
    function close() {
      emit('close');
    }
    
    function addVariable() {
      if (!isValidNewVariable.value) return;
      
      const updatedVariables = [...props.variables, { ...newVariable.value }];
      emit('update:variables', updatedVariables);
      
      // Reset the form
      newVariable.value = {
        name: '',
        category: '',
        description: '',
        exampleValue: ''
      };
      
      logger.info(`New variable "${newVariable.value.name}" added`);
    }
    
    function editVariable(index: number) {
      const variable = filteredVariables.value[index];
      const originalIndex = props.variables.findIndex(v => 
        v.name === variable.name && 
        v.category === variable.category
      );
      
      if (originalIndex !== -1) {
        editingVariable.value = originalIndex;
        editedVariable.value = { ...props.variables[originalIndex] };
      }
    }
    
    function saveEdit() {
      if (editingVariable.value === null) return;
      
      const updatedVariables = [...props.variables];
      updatedVariables[editingVariable.value] = { ...editedVariable.value };
      emit('update:variables', updatedVariables);
      
      logger.info(`Variable "${editedVariable.value.name}" updated`);
      cancelEdit();
    }
    
    function cancelEdit() {
      editingVariable.value = null;
    }
    
    function confirmDelete(index: number) {
      const variable = filteredVariables.value[index];
      
      // Don't delete system variables
      if (variable.isSystem) return;
      
      // Store the original index for deletion
      const originalIndex = props.variables.findIndex(v =>
        v.name === variable.name &&
        v.category === variable.category
      );
      
      if (originalIndex !== -1) {
        deletingVariableIndex.value = originalIndex;
      }
    }
    
    function deleteVariable() {
      if (deletingVariableIndex.value === null) return;
      
      const variableName = props.variables[deletingVariableIndex.value].name;
      
      const updatedVariables = [...props.variables];
      updatedVariables.splice(deletingVariableIndex.value, 1);
      emit('update:variables', updatedVariables);
      
      logger.info(`Variable "${variableName}" deleted`);
      
      // Reset the deleting state
      cancelDelete();
    }
    
    function cancelDelete() {
      deletingVariableIndex.value = null;
    }
    
    return {
      selectedCategory,
      editingVariable,
      editedVariable,
      newVariable,
      isValidNewVariable,
      uniqueCategories,
      filteredVariables,
      close,
      addVariable,
      editVariable,
      saveEdit,
      cancelEdit,
      confirmDelete,
      deleteVariable,
      cancelDelete,
      deletingVariableIndex
    };
  }
});
</script>